import { FileDownload } from "@mui/icons-material";
import { Box, Grid } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DropdownMenu from "../../../App/Components/DropdownMenu/DropdownMenu";
import { ExportAttachmentsModal } from "../../../App/Components/Export/ExportAttachmentsModal";
import { RandomTextSkeleton } from "../../../App/Components/Skeleton/RandomTextSkeleton";
import CrmTable from "../../../App/Components/Table/CrmTable";
import { ISelectedJobAlert } from "../../../App/Typings/Campaigns";
import { Rule } from "../../../App/Typings/DynamicGroupRules";
import { ITablePagination } from "../../../App/Typings/SortableTable";
import { clearOldLocalStorage } from "../../../App/Utilities/browserStorage";
import CandidatesCapturedFilters from "./CandidatesCapturedFilters";
import CandidatesCapturedSearchInput from "./CandidatesCapturedSearchInput";
import useCandidatesCapturedState from "./CandidatesCapturedState";
import FilterModal from "./FilterModal";

export default function CandidatesCaptured({
  campaignId,
  formId,
  formVersion,
}: {
  campaignId: string;
  formId: string;
  formVersion: string;
}) {
  const navigate = useNavigate();
  const {
    getters: {
      isFilterModalOpen,
      campaignResponses,
      campaignResponsesLoading,
      columnNames,
      formFields,
      isFormFieldsLoading,
      search,
      order,
      orderBy,
      page,
      pageSize,
      rules,
    },
    handlers: {
      handleChangePage,
      handleChangeRowsPerPage,
      handleCloseFilterModal,
      handleOpenFilterModal,
      handleRequestSort,
      handleResetRules,
      handleRemoveRule,
      handleSetRules,
      handleSetSearch,
      handleDownloadResponses,
      handleDownloadResponseAttachments,
    },
  } = useCandidatesCapturedState(campaignId, formId, true);
  const [exportAttachmentsModalOpen, setExportAttachmentsModalOpen] = useState<boolean>(false);

  const tableKey = `campaign-responses-${campaignId}`;
  const tableKeyWithVersion = `${tableKey}-${formVersion}`;

  const items: Record<string, any>[] = useMemo(
    () =>
      campaignResponses?.items.map((i) => {
        return {
          ...i?.candidateResponse,
          id: i.id,
          interestPicker: getSelectedJobAlertsDisplay(i.selectedJobAlerts),
          fieldLabels: i?.fieldLabels ?? {},
        };
      }) ?? [],
    [campaignResponses],
  );
  const itemCount = items.length;
  const totalCount = campaignResponses?.totalItems ?? 0;
  const totalCountDisplay = totalCount > pageSize ? totalCount : itemCount;

  const navigateToCandidate = (data: Record<string, string | Date | string[]>) => {
    navigate(`/candidates/${data.candidateId}`);
  };

  useEffect(() => {
    const currentKeys = Object.keys(localStorage).filter((key) => key.startsWith(tableKey));
    for (const key of currentKeys.filter((k) => !k.includes(formVersion))) {
      localStorage.removeItem(key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={{ pt: 2 }}>
      <Grid container gap={2}>
        <Grid item xs={12} md={4}>
          <CandidatesCapturedSearchInput
            dataTestId="campaign-responses-searchbar"
            defaultSearch={search}
            handleSetSearch={handleSetSearch}
          />
        </Grid>
        <FilterModal
          isLoading={isFormFieldsLoading}
          groups={[]}
          queryRules={rules}
          formFields={formFields ?? []}
          isOpen={isFilterModalOpen}
          handleSetRules={handleSetRules}
          handleOpenModal={handleOpenFilterModal}
          handleCloseModal={handleCloseFilterModal}
          handleResetRules={handleResetRules}
          handleRemoveRule={handleRemoveRule}
          isCampaign={true}
          campaignId={campaignId}
          LoadSaveFilters={
            <CandidatesCapturedFilters
              isLoading={campaignResponsesLoading}
              rules={rules}
              handleSetRules={handleSetRules}
              campaignId={campaignId}
            />
          }
          campaigns={[]}
          tags={[]}
        />
      </Grid>
      {columnNames && columnNames.length > 0 && (
        <>
          <CrmTable
            name={tableKeyWithVersion}
            columns={columnNames}
            loading={campaignResponsesLoading}
            data={items}
            pagination={{
              ...paginationOptions,
              currentPage: page,
              rows: pageSize,
              handleChangePage: handleChangePage,
              handleChangeRowsPerPage: handleChangeRowsPerPage,
              totalCount: totalCountDisplay,
              labelDisplayedRows: ({ from, to, count }) =>
                renderTablePaginationDisplay({ from, to, count, campaignResponsesLoading, rules, search }),
            }}
            onRowClick={(row) => navigate(`/candidates/${row.candidateId}`)}
            order={order}
            orderBy={orderBy}
            onSort={handleRequestSort}
            rowTitle={"View Candidate Profile"}
            customCell={{
              elementAttributes: (props) => ({
                onClick: (_e, extendedEvent) => {
                  const { childProps } = extendedEvent;
                  if (
                    childProps.column.key !== "actions" &&
                    childProps.column.key !== "selection-cell" &&
                    childProps.column.key !== "tags"
                  ) {
                    navigateToCandidate(childProps.rowData);
                  }
                },
                "data-test": `item-${props.column.key}-${props.rowData.id}`,
              }),
              content(props) {
                // If column key ends in a number `_#`, render if the column label matches the props.rowData.fieldLabels dictionary
                const hasFieldLabels =
                  props.rowData.fieldLabels && Object.keys(props.rowData.fieldLabels).length > 0;
                if (hasFieldLabels) {
                  const columnKey = props.column.key.replace(/_\d+$/, "");
                  const fieldLabel = props.rowData.fieldLabels[columnKey];
                  // Render text only if the fieldLabel matches the label on props.column
                  if (fieldLabel && fieldLabel === props.column.title) {
                    return <div>{props.rowData[columnKey]}</div>;
                  } else if (fieldLabel && fieldLabel !== props.column.title) {
                    return <></>;
                  } else {
                    return;
                  }
                }
              },
            }}
            ExportButton={(cols) => (
              <DropdownMenu
                buttonLabel="Export"
                tooltipText="Export candidate data or attachments"
                buttonIcon={<FileDownload color="primary" />}
                menuItems={[
                  {
                    label: "Candidate Data",
                    title: "Export candidate data using your current filters",
                    onClick: handleDownloadResponses,
                  },
                  {
                    label: "Candidate Attachments",
                    title: "Export candidate attachments using your current filters",
                    onClick: () => setExportAttachmentsModalOpen(true),
                  },
                ]}
              />
            )}
            //rowTestId={(row) => `campaign-responses-${row.candidateId}`}
          />
          <ExportAttachmentsModal
            isOpen={exportAttachmentsModalOpen}
            handleCloseModal={() => setExportAttachmentsModalOpen(false)}
            onExportAttachmentsClick={async () => {
              await handleDownloadResponseAttachments();
              setExportAttachmentsModalOpen(false);
            }}
          />
        </>
      )}
    </Box>
  );
}

function getSelectedJobAlertsDisplay(jobAlerts: ISelectedJobAlert[] | undefined): string {
  if (!jobAlerts || jobAlerts.length === 0) {
    return "";
  }
  return jobAlerts
    .filter((jobAlert) => jobAlert.adGroup?.id !== "")
    .map((jobAlert) => jobAlert.adGroup?.name)
    .join(", ");
}

const renderTablePaginationDisplay = ({
  from,
  to,
  count,
  campaignResponsesLoading,
  rules,
  search,
}: {
  from: number;
  to: number;
  count: number;
  campaignResponsesLoading: boolean;
  rules: Rule[];
  search: string;
}) => {
  if (campaignResponsesLoading) {
    return <RandomTextSkeleton />;
  }
  if (count === 0) {
    return "---";
  }
  if (rules.some((r) => r.filter.type !== "") || search !== "") {
    return `${from}-${to} of ${count} form submissions (filtered)`;
  }
  return `${from}-${to} of ${count} total form submissions`;
};

const paginationOptions: ITablePagination = {
  rowsPerPage: [10, 25, 50, 100],
  enabled: true,
};

clearOldLocalStorage("campaign-responses");
